import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { ButtonLink } from "../../../../components/ui/button";
import { AppLink } from "../../../../components/ui/link";
import { CTASection } from "../landing/CTASection";
import { AppDownloadSection } from "../landing/AppDownloadSection";
import { LandingFooter } from "../landing/LandingFooter";
import { FadeUp } from "../landing/FadeUp";
import { useLandingScrollToSection } from "./useLandingScrollToSection";
import {
  PRODUCT_LANDING_DEFINITIONS,
  PRODUCT_LANDING_ORDER,
  type ProductLandingSlug,
} from "./productLandingContent";
import type { AppStoreLinks, RouteMap, UserDisplay } from "../../types";

type ProductLandingPageProps = {
  product: ProductLandingSlug;
  loggedIn: boolean;
  hasBusiness: boolean;
  hasOrganization?: boolean;
  canAccessBackend?: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
  appStore?: AppStoreLinks | null;
};

export const ProductLandingPage: React.FC<ProductLandingPageProps> = ({
  product,
  loggedIn,
  hasBusiness,
  hasOrganization,
  canAccessBackend,
  routes,
  csrfToken,
  page,
  userDisplay,
  appStore,
}) => {
  const definition = PRODUCT_LANDING_DEFINITIONS[product];
  const KickerIcon = definition.kickerIcon;
  const scrollToSection = useLandingScrollToSection();
  const others = PRODUCT_LANDING_ORDER.filter((slug) => slug !== product).map(
    (slug) => PRODUCT_LANDING_DEFINITIONS[slug],
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="landing"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        hasOrganization={hasOrganization}
        canAccessBackend={canAccessBackend}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
        scrollToSection={scrollToSection}
      />

      <section className="bg-background pt-20 pb-12 sm:pt-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <FadeUp>
            <span className="inline-flex items-center gap-2 rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
              <KickerIcon className="h-4 w-4" aria-hidden />
              {definition.kicker}
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {definition.heroH1}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {definition.heroSubhead}
            </p>
            <p className="mt-4 text-base font-medium text-foreground sm:text-lg">
              {definition.standAlone}
            </p>
            <div className="mt-8">
              <ButtonLink
                to={routes.signup}
                size="lg"
                variant="premium"
                className="px-8 py-4 text-lg group"
              >
                Start 30-day trial
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </ButtonLink>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-border shadow-xl">
              <img
                src={definition.image}
                alt={definition.imageAlt}
                className="h-full w-full object-cover"
                width={1600}
                height={1067}
                decoding="async"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-card py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <h2 className="mb-12 font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              What you get
            </h2>
          </FadeUp>
          <div className="grid gap-8 md:grid-cols-2">
            {definition.benefits.map((benefit, index) => (
              <FadeUp key={benefit.title} delay={0.05 * (index + 1)}>
                <div className="flex gap-4">
                  <CheckCircle2
                    className="mt-1 h-6 w-6 shrink-0 text-premium-500"
                    aria-hidden
                  />
                  <div>
                    <h3 className="font-display text-xl font-bold text-foreground">
                      {benefit.title}
                    </h3>
                    <p className="mt-2 leading-relaxed text-muted-foreground">
                      {benefit.body}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <AppDownloadSection
        routes={routes}
        appStore={appStore}
        pitch={definition.mobilePitch}
      />

      <section className="bg-background py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <h2 className="font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Use one or all three. Same $9.
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              The trial and the plan include the suite. Use what you need.
            </p>
          </FadeUp>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {others.map((other) => {
              const Icon = other.kickerIcon;
              return (
                <AppLink
                  key={other.slug}
                  to={other.href}
                  className="group flex h-full items-start gap-4 rounded-xl border border-border bg-card p-6 text-foreground no-underline transition-all hover:-translate-y-0.5 hover:border-premium-300 hover:text-foreground hover:no-underline hover:shadow-md"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-premium-100 bg-premium-50 text-premium-600">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold text-foreground">
                      {other.navLabel}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {other.standAlone}
                    </p>
                    <span className="mt-3 inline-flex items-center text-sm font-semibold text-premium-600">
                      See {other.navLabel.toLowerCase()}
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </AppLink>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection routes={routes} />
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        hasOrganization={hasOrganization}
        canAccessBackend={canAccessBackend}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
